import { Badge } from "@package/subframe/components/Badge";
import { Table } from "@package/subframe/components/Table";
import { FeatherClipboardCheck, FeatherShieldCheck } from "@subframe/core";

import { CategoryPill } from "@/components/ui/category-pill";
import { TintedEntityIcon } from "@/components/ui/entity-icon";
import { SortableHeaderCell } from "@/components/ui/sortable-header-cell";
import { TABLE_MIN_WIDTH } from "@/components/ui/table-scroll-container";
import type { EvidenceRecordResponseDto } from "@/generated/model/evidenceRecordResponseDto";
import { EvidenceSourceType } from "@/generated/model/evidenceSourceType";
import { EvidenceStatus } from "@/generated/model/evidenceStatus";
import {
  completedByLabel,
  completedDateLabel,
  dueDateLabel,
  type EvidenceSortColumn,
  type SortDir,
  SOURCE_ENTITY,
  SOURCE_LABELS,
  STATUS_DISPLAY,
} from "@/lib/evidence-hub";

interface EvidenceHubTableProps {
  items: EvidenceRecordResponseDto[];
  activeColumn: EvidenceSortColumn;
  activeDir: SortDir;
  onSort: (column: EvidenceSortColumn) => void;
  onView: (record: EvidenceRecordResponseDto) => void;
}

const FALLBACK_GLYPHS: Record<
  EvidenceSourceType,
  React.ReactElement<{ style?: React.CSSProperties }>
> = {
  [EvidenceSourceType.COMPLIANCE_TASK]: <FeatherShieldCheck />,
  [EvidenceSourceType.CHECKLIST]: <FeatherClipboardCheck />,
};

export function EvidenceHubTable({
  items,
  activeColumn,
  activeDir,
  onSort,
  onView,
}: EvidenceHubTableProps) {
  return (
    <Table
      className={TABLE_MIN_WIDTH.lg}
      header={
        <Table.HeaderRow>
          <SortableHeaderCell
            column="date"
            label="Date"
            activeColumn={activeColumn}
            activeDir={activeDir}
            onSort={onSort}
          />
          <SortableHeaderCell
            column="type"
            label="Type"
            activeColumn={activeColumn}
            activeDir={activeDir}
            onSort={onSort}
          />
          <SortableHeaderCell
            column="activity"
            label="Activity / Record"
            activeColumn={activeColumn}
            activeDir={activeDir}
            onSort={onSort}
          />
          <SortableHeaderCell
            column="category"
            label="Category"
            activeColumn={activeColumn}
            activeDir={activeDir}
            onSort={onSort}
          />
          <SortableHeaderCell
            column="status"
            label="Status"
            activeColumn={activeColumn}
            activeDir={activeDir}
            onSort={onSort}
          />
          <SortableHeaderCell
            column="dueDate"
            label="Due Date"
            activeColumn={activeColumn}
            activeDir={activeDir}
            onSort={onSort}
          />
          <SortableHeaderCell
            column="completedBy"
            label="Completed By"
            activeColumn={activeColumn}
            activeDir={activeDir}
            onSort={onSort}
          />
        </Table.HeaderRow>
      }
    >
      {items.map((record) => (
        <Table.Row
          key={record.recordId}
          clickable
          onClick={() => onView(record)}
        >
          <Table.Cell className="h-14 grow shrink-0 basis-0">
            <div className="flex items-center gap-3">
              <TintedEntityIcon
                entity={SOURCE_ENTITY[record.sourceType]}
                slug={record.category?.slug}
                color={record.category?.color}
                fallbackIcon={FALLBACK_GLYPHS[record.sourceType]}
                title={`${SOURCE_LABELS[record.sourceType]}${record.category ? ` — ${record.category.name}` : ""}`}
              />
              <span className="text-caption font-caption text-neutral-900">
                {completedDateLabel(record.completedAt)}
              </span>
            </div>
          </Table.Cell>
          <Table.Cell className="h-14 grow shrink-0 basis-0">
            <span className="text-caption font-caption text-neutral-900">
              {SOURCE_LABELS[record.sourceType]}
            </span>
          </Table.Cell>
          <Table.Cell className="h-14 grow shrink-0 basis-0">
            <span className="text-body-bold font-body-bold text-neutral-900">
              {record.name || "—"}
            </span>
          </Table.Cell>
          <Table.Cell className="h-14 grow shrink-0 basis-0">
            {record.category ? (
              <CategoryPill
                name={record.category.name}
                slug={record.category.slug}
                color={record.category.color}
              />
            ) : (
              <span className="text-caption font-caption text-subtext-color">
                —
              </span>
            )}
          </Table.Cell>
          <Table.Cell className="h-14 grow shrink-0 basis-0">
            <Badge
              variant={
                record.status === EvidenceStatus.COMPLETED_LATE
                  ? "warning"
                  : "success"
              }
            >
              {STATUS_DISPLAY[record.status] ?? record.status}
            </Badge>
          </Table.Cell>
          <Table.Cell className="h-14 grow shrink-0 basis-0">
            <span className="text-caption font-caption text-neutral-900">
              {dueDateLabel(record.dueDate)}
            </span>
          </Table.Cell>
          <Table.Cell className="h-14 grow shrink-0 basis-0">
            <span className="text-caption font-caption text-neutral-900">
              {completedByLabel(record.completedBy)}
            </span>
          </Table.Cell>
        </Table.Row>
      ))}
    </Table>
  );
}
